import React, { memo, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message, Row } from "antd";
import moment from "moment";
import { PRIVATE_ROUTES } from "app/routers/Router.config";
import { DateFormats } from "app/constants/dateConst";
import { ITabWithModalType } from "app/types/tabType";
import Permission, {
  hasPermission,
} from "app/components/Permission/Permission";
import { PermissionEnum } from "app/constants/permissionConst";
import Table, { Column, RowEditButtons } from "app/components/Table/Table";
import Button from "app/components/Button/Button";
import SubstancesModal from "./components/SubtancesModal/SubstancesModal";
import styles from "./SubstancesTabPane.module.scss";
import { useFetchPaginatedData, useDeleteData } from "app/hooks/hooks";
import qs from "query-string";
import { getOrderBy } from "app/helpers/apiHelper";
import { PaginationConfig } from "antd/lib/table";
import { substancesGet, substancesDelete } from "app/api/v2/substancesApi";
import {
  SubstancesApiV2SubstancesGetRequest,
  ListWrapperSubstanceDTO,
} from "@generated/v2";
import { randomMaxMin } from "app/helpers/utilHelper";
import ErrorFallbackUI from "app/components/ErrorFallbackUI";
import TabPane from "app/components/Tabs/TabPane";

interface IProps extends ITabWithModalType {
  /** Boolean to indicate if current tab is active */
  activeTab: boolean;
}
/**
 *
 * Substance Tab pane
 */
const SubstancesTabPane = ({
  redirect,
  modalConfig,
  activeTab,
  ...rest
}: IProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();

  /** Delete data hook */
  const {
    loading: deleteLoading,
    isSuccess: deleteSuccess,
    isError: deleteError,
    id: deleteId,
    deleteData,
  } = useDeleteData<void>(substancesDelete);

  /** get substances data hook */
  const {
    data: substances,
    pagination,
    orderBy,
    loading,
    refetch,
  } = useFetchPaginatedData<
    ListWrapperSubstanceDTO,
    SubstancesApiV2SubstancesGetRequest
  >(substancesGet, {
    withOrderBy: activeTab,
  });

  /** Listens for substance deletion success, or error */
  useEffect(() => {
    if (deleteSuccess) {
      message.success(t("substances.deleteSuccess"));
      refetch();
    } else if (deleteError) {
      message.error(t("substances.deleteError"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteSuccess, deleteError]);

  const handleTableChange = (pagination: PaginationConfig, filters, sorter) => {
    // Keep the old search params
    const currentSearch = qs.parse(location.search);

    // and overwrite or add new values
    const values = {
      ...currentSearch,
      page: pagination.current,
      orderBy:
        (sorter.order && getOrderBy(sorter.columnKey, sorter.order)) ||
        undefined,
    };

    history.push({
      pathname: location.pathname,
      search: qs.stringify(values, { arrayFormat: "comma" }),
    });
  };

  return (
    <ErrorFallbackUI>
      <TabPane {...rest}>
        <Permission
          requiredPermissions={[PermissionEnum.TEST_TYPES_SUBSTANCES_WRITE]}
        >
          <Row type="flex" justify="end">
            <Button
              className={styles.button}
              type="primary"
              size="large"
              onClick={() =>
                redirect(PRIVATE_ROUTES.TEST_TYPES_CREATE_SCREEN.path)
              }
              icon="plus"
            >
              {t("substances.buttonCreate")}
            </Button>
          </Row>

          <SubstancesModal
            show={modalConfig && modalConfig.show ? modalConfig.show : false}
            data={
              modalConfig &&
              substances.find(item => item?.id?.toString() === modalConfig.edit)
            }
            close={() => redirect(PRIVATE_ROUTES.TEST_TYPES_SCREEN.path)}
            submit={refetch}
            key={`substanceCreated-${randomMaxMin()}`}
          />
        </Permission>
        <Table
          className={styles.table}
          dataSource={substances}
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
        >
          <Column
            title={t("substances.columnName")}
            key="name"
            dataIndex="name"
            sorter
            sortOrder={
              (orderBy.key === "name" && orderBy.direction) || undefined
            }
          />
          <Column
            title={t("substances.columnCreated")}
            key="created"
            dataIndex="created"
            width={300}
            sorter
            sortOrder={
              (orderBy.key === "created" && orderBy.direction) || undefined
            }
            render={text => moment(text).format(DateFormats.DATE_TIME)}
          />
          {hasPermission([
            PermissionEnum.TEST_TYPES_SUBSTANCES_WRITE,
            PermissionEnum.TEST_TYPES_TEST_ITEMS_DELETE,
          ]) && (
            <Column
              key="actions"
              fixed="right"
              width={130}
              render={(text: any, record: any) => (
                <RowEditButtons
                  id={record.id}
                  onEdit={() =>
                    redirect(
                      PRIVATE_ROUTES.TEST_TYPES_EDIT_SCREEN.path,
                      record.id
                    )
                  }
                  onDelete={deleteData}
                  deleteTitle={t("substances.columnDeleteConfirm")}
                  deleteConfirm={t("substances.columnDeleteOk")}
                  deleteCancel={t("substances.columnDeleteCancel")}
                  deleteLoading={deleteId === record.id && deleteLoading}
                />
              )}
            />
          )}
        </Table>
      </TabPane>
    </ErrorFallbackUI>
  );
};

export default memo(SubstancesTabPane);
